/**
 * CommandSelector Component
 *
 * Dropdown selector for MAVLink commands, grouped by category
 */

import { useState, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import {
  MavCommand,
  COMMAND_INFO,
  getCommandName,
  isCommandSupportedForVehicle,
} from '@/constants/mavlink-commands';
import { VehicleType } from '@/features/mission/types/mission.types';
import { cn } from '@/lib/utils';

interface CommandSelectorProps {
  value: MavCommand;
  onChange: (command: MavCommand) => void;
  vehicleType?: VehicleType;
  className?: string;
  disabled?: boolean;
}

interface CommandGroup {
  category: string;
  label: string;
  commands: { id: MavCommand; name: string; description: string }[];
}

export function CommandSelector({
  value,
  onChange,
  vehicleType,
  className,
  disabled = false,
}: CommandSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Group commands by category
  const commandGroups = useMemo<CommandGroup[]>(() => {
    const groups: CommandGroup[] = [
      {
        category: 'navigation',
        label: 'Navigation Commands',
        commands: [],
      },
      {
        category: 'do',
        label: 'Do Commands',
        commands: [],
      },
      {
        category: 'condition',
        label: 'Condition Commands',
        commands: [],
      },
      {
        category: 'camera',
        label: 'Camera Commands',
        commands: [],
      },
    ];

    // Populate groups from COMMAND_INFO
    Object.values(COMMAND_INFO).forEach((info) => {
      if (!info) return;

      // Filter by vehicle type if specified
      if (vehicleType && !isCommandSupportedForVehicle(info.id, vehicleType)) {
        return;
      }

      const group = groups.find((g) => g.category === info.category);
      if (group) {
        group.commands.push({
          id: info.id,
          name: info.name,
          description: info.description,
        });
      }
    });

    // Remove empty groups
    return groups.filter((g) => g.commands.length > 0);
  }, [vehicleType]);

  // Filter commands by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return commandGroups;

    const query = searchQuery.toLowerCase();
    return commandGroups
      .map((group) => ({
        ...group,
        commands: group.commands.filter(
          (cmd) =>
            cmd.name.toLowerCase().includes(query) ||
            cmd.description.toLowerCase().includes(query)
        ),
      }))
      .filter((g) => g.commands.length > 0);
  }, [commandGroups, searchQuery]);

  const selectedCommand = COMMAND_INFO[value];
  const selectedName = selectedCommand?.name || getCommandName(value);

  const handleSelect = (command: MavCommand) => {
    onChange(command);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2',
          'bg-slate-800 text-white rounded-md border border-slate-600',
          'focus:outline-none focus:ring-2 focus:ring-blue-500',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'hover:bg-slate-700 transition-colors'
        )}
      >
        <span className="text-sm font-medium">{selectedName}</span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-slate-400 transition-transform',
            isOpen && 'transform rotate-180'
          )}
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Content */}
          <div className="absolute z-40 mt-1 w-full max-w-md bg-slate-800 border border-slate-600 rounded-md shadow-xl max-h-96 overflow-hidden flex flex-col">
            {/* Search Box */}
            <div className="p-2 border-b border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search commands..."
                  className="w-full pl-9 pr-3 py-2 bg-slate-900 text-white text-sm rounded border border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Command Groups */}
            <div className="overflow-y-auto">
              {filteredGroups.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-400">
                  No commands found
                </div>
              ) : (
                filteredGroups.map((group) => (
                  <div key={group.category} className="py-2">
                    {/* Group Header */}
                    <div className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                      {group.label}
                    </div>

                    {/* Commands in Group */}
                    {group.commands.map((cmd) => (
                      <button
                        key={cmd.id}
                        type="button"
                        onClick={() => handleSelect(cmd.id)}
                        className={cn(
                          'w-full px-3 py-2 text-left hover:bg-slate-700 transition-colors',
                          value === cmd.id && 'bg-blue-600/20 text-blue-300'
                        )}
                      >
                        <div className="text-sm font-medium text-white">
                          {cmd.name}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {cmd.description}
                        </div>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
